/* eslint-disable @typescript-eslint/ban-types */
import { FC, useEffect, useState, ReactNode } from "react"
import { UsersService, User, RequestPaginator } from "../sdk"
import Table from "../components/Table"
import useError from "../hooks/useError"
import classNames from "classnames"
import Pagination from "../components/Pagination"
import { Link } from "react-router-dom"
import { userStatusMap } from "../constants/user"
import PageLoader from "../components/PageLoader"
import useDebounce from "../hooks/useDebounce"
import Input from "../components/Input"
import Icon from "../components/Icon"
import Select from "../components/Select"
import { sortersMap, sortersValuesMap } from "../constants/sorters"
import Col from "../components/Col"

type Props = {
  sorters?: Record<string, unknown>
  filters?: Record<string, unknown>
}

type SortersAndFiltersType = {
  properties: {
    [key: string]: {
      type: string
      format?: string
    }
  }
}

const paginatorLimit = 20

const Users: FC<Props> = () => {
  const [users, setUsers] = useState<User[] | null>(null)

  const [sorters, setSorters] = useState<string[] | null>(null)
  const [currentSorter, setCurrentSorter] = useState<string | null>("id")
  const [currentSorterValue, setCurrentSorterValue] = useState<string | null>("DESC")

  const [paginator, setPaginator] = useState<RequestPaginator>({ offset: 0, limit: paginatorLimit })
  const [pagination, setPagination] = useState<{ current: number, total: number } | null>({ current: 1, total: 1 })
  const [userCount, setUserCount] = useState<number | null>(null)

  const [firstLoading, setFirstLoading] = useState<boolean>(true)
  const [tableUpdatingLoading, setTableUpdatingLoading] = useState<boolean>(false)

  const [searchBarValue, setSearchBarValue] = useState<string>("")
  const debouncedValue = useDebounce(searchBarValue, 500)

  const { handleError } = useError()

  const getUsers = async(): Promise<void> => {
    try {
      if (!firstLoading) {
        setTableUpdatingLoading(true)
      }
      const { data } = await UsersService.listUsers({
        paginator,
        sorters: currentSorter ? {
          [currentSorter]: currentSorterValue
        } : {},
        search: debouncedValue === "" ? null : debouncedValue
      })
      setUsers(data)
    } catch (err) {
      handleError(err)
    }
    setTableUpdatingLoading(false)
    setFirstLoading(false)
  }

  useEffect(() => {
    void (async(): Promise<void> => {
      await getUsers()
    })()
  }, [currentSorter, currentSorterValue, paginator, debouncedValue])

  useEffect(() => {
    void (async(): Promise<void> => {
      try {
        const { sorters, count } = await UsersService.listUsers({
          paginator: {
            offset: 0,
            limit: 0
          },
          getSorters: true,
          getCount: true,
          search: debouncedValue === "" ? null : debouncedValue
        })

        sorters && setSorters(Object.keys((sorters as SortersAndFiltersType).properties))
        setUserCount(count || 0)
      } catch (err) {
        handleError(err)
      }
    })()
  }, [debouncedValue])

  useEffect(() => {
    if (userCount && Math.ceil(userCount / paginatorLimit)) {
      setPagination({ current: 1, total: Math.ceil(userCount / paginatorLimit) })
    }
  }, [userCount])

  useEffect(() => {
    if (pagination && (pagination?.current - 1) * paginatorLimit !== paginator.offset) {
      setPaginator({ offset: (pagination?.current - 1) * paginatorLimit, limit: paginatorLimit })
    }
  }, [pagination, debouncedValue])

  return (
    <>
      {firstLoading ?
        <PageLoader className="h-screen w-screen top-0 left-0 fixed"/>
        :
        <>

          <h1 className="text-3xl font-semibold mb-8">Utenti</h1>

          <div className="w-1/3 grid grid-cols-12 gap-x-3">
            {sorters &&
              <>
                <Col lg={6}>
                  <Select
                    label="Ordina per"
                    defaultValue={{ label: sortersMap[currentSorter!], value: currentSorter }}
                    options={sorters.map((sorter) => {
                      return { label: sortersMap[sorter], value: sorter }
                    })}
                    onChange={(value): void => setCurrentSorter((value as { value: string }).value)}
                  />
                </Col>

                <Col lg={6}>
                  <Select
                    className="mt-6"
                    defaultValue={{ label: sortersValuesMap[currentSorterValue!], value: currentSorterValue }}
                    options={["ASC", "DESC"].map((option) => {
                      return { label: sortersValuesMap[option], value: option }
                    })}
                    onChange={(value): void => setCurrentSorterValue((value as { value: string }).value)}
                  />
                </Col>
              </>
            }
          </div>

          <div className="w-2/4 mt-4 mb-6">
            <Input
              value={searchBarValue}
              onChange={setSearchBarValue}
              placeholder="Cerca..."
              suffix={
                searchBarValue !== "" &&
                <span
                  onClick={(): void => {
                    setSearchBarValue("")
                  }}
                  className="hover:cursor-pointer hover:text-gray-500 w-8 h-8 flex justify-center items-center"
                >
                  <Icon
                    name="x-close"
                    size={20}
                  />
                </span>
              }
            />
          </div>


          {!firstLoading && users && userCount &&
            <>
              <Table
                className={classNames(tableUpdatingLoading && "opacity-50 pointer-events-none", "users-list__table")}
                headings={["ID", "Username", "Email", "Cognome", "Nome", "Stato", ""]}
                fixedColumns={["ID", ""]}
                rows={users.map(({
                  id,
                  username,
                  email,
                  firstname,
                  lastname,
                  status
                }): { id: string, children: ReactNode[] } => {
                  return {
                    id: id.toString(),
                    children: [
                      <span>{id}</span>,
                      <span>{username}</span>,
                      <span>{email}</span>,
                      <span>{lastname}</span>,
                      <span>{firstname}</span>,
                      <span className={classNames("font-semibold")}>
                        {userStatusMap[status]}
                      </span>,
                      <Link to={`/user/${id}`}>
                        <span className="underline">Visualizza</span>
                      </Link>
                    ]
                  }
                })}
              />
              {pagination && userCount > paginatorLimit &&
                <Pagination
                  className={classNames("mt-4", tableUpdatingLoading && "opacity-50 pointer-events-none")}
                  total={pagination.total}
                  current={pagination.current}
                  onChange={(page): void => {
                    if (page > 0 && page <= pagination.total && page !== pagination.current) {
                      setPagination({ current: page, total: pagination.total })
                    }
                  }}
                />
              }
            </>
          }
        </>
      }
    </>
  )
}

export default Users